import React, { useEffect, useState } from "react";
import { useAppContext } from "../utils/context/app-context";
import {
	ContentContainer,
	TitleHeader,
	Seperator,
	Button,
	AlertMessage,
} from "../components/UiComponents";
import {
	CourseHeader,
	CourseTaskList,
	QuickLink,
	CourseChecklist,
	CourseCommentsBox,
	CourseWrapper,
	isLastDayOfCourse,
} from "../components/CourseComponents";
import { Link } from "react-router-dom";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";


function CoursePage() {

	const { state, api, dispatch } = useAppContext();
	const isOffline = state.offline;
	const [courseSlug, setCourseSlug] = useState("");
	useDocumentTitle("Course");
	const date = api.getCurrentDate();
	const course = api.getCurrentCourse();
	const [closingCourse, setClosingCourse] = useState(false);
	const [closingCourseSuccess, setClosingCourseSuccess] = useState(false);
	const [closingCourseError, setClosingCourseError] = useState(false);
	const isReadyOnly = useUserIsReadOnly();

	useEffect(() => {
		api.loadCourses();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		let course = api.getCurrentCourse();
		if (course) {
			setCourseSlug(`/courses/${course.id}`);
			updateTutorComments()
		}
	}, [state.current_course_id]);

	useEffect(() => {
		if (closingCourseSuccess) {
			setTimeout(() => {
				setClosingCourseSuccess(false);
			}, 2000);
		}
	}, [closingCourseSuccess]);

	async function updateTutorComments() {
		if (isOffline) {
			return;
		}
		// eslint-disable-next-line no-unused-vars
		const { data, error } = await api.fetchData(
			`/courses/${course.id}/tutor-comments`
		);

		if (data?.comments) {
			dispatch({
				type: "UPDATE_COURSE_COMMENTS",
				payload: {
					course_id: course.id,
					comments: data.comments,
				},
			});
		}
	}

	async function handleCloseCourse() {
		if (
			!window.confirm(
				"Are you sure you want to submit this course as completed?"
			)
		) {
			return;
		}
		setClosingCourse(true);

		const dataToPost = {
			course_id: course.id,
		};
		const { data, error } = await api.fetchData(
			`/courses/mark-completed`,
			"POST",
			dataToPost
		);
		console.log(data);
		if (data?.success) {
			// After 2 seconds, set the course as completed
			setTimeout(() => {
				dispatch({
					type: "SET_COURSE_COMPLETED",
					payload: {
						course_id: course.id,
					},
				});
			}, 2000);
			setClosingCourse(false);
			setClosingCourseSuccess(true);
		} else {
			setClosingCourseError(error || data?.error || "An error occurred");
			setClosingCourse(false);
		}
	}

	return (
		<div className="view-course">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<div className="row">
						<div className="col-sm-8">
							{course?.tutor_completed_at && (
								<div className="mb-4">
									<AlertMessage
										type="success"
										message="This Course has been submitted as completed"
									/>
								</div>
							)}
							<TitleHeader title="Your Tasks" />
							<CourseTaskList course={course} date={date} />
							{isLastDayOfCourse(course) &&
								!course?.tutor_completed_at && (
									<>
										<Button
											icon="verified"
											text={
												closingCourseSuccess
													? "Course submitted as completed"
													: (closingCourse ? "Submitting Course..." : "Submit Course as Completed")
											}
											type="success"
											className="mt-4"
											disabled={
												closingCourse || closingCourseSuccess || isReadyOnly || isOffline
											}
											onClick={handleCloseCourse}
										/>
										{closingCourseError && (
											<div className="mt-4">
												<AlertMessage
													type="danger"
													message={closingCourseError}
												/>
											</div>
										)}
									</>
								)}
							<Seperator />
							{course?.witness_incidents &&
								Array.isArray(course.witness_incidents) &&
								course.witness_incidents.length > 0 && (
									<>
										<TitleHeader title="Incidents Witnessed" />
										<p>
											Please confirm you witnessed the following
											incidents
										</p>
										<div className="witness-incidents-grid">
											{course.witness_incidents.map((incident) => (
												<div
													key={incident.id}
													className="witness-incident">
													<div className="witness-incident__name">
														{incident.name_of_person}
													</div>
													<Link
														to={`${courseSlug}/witness-incident/${incident.id}`}
														className="btn btn-primary btn-sm">
														View Incident
													</Link>
												</div>
											))}
										</div>
										<Seperator />
									</>
								)}
							<TitleHeader title="Quick Links" />
							<div className="course-quick-links">
								<QuickLink
									text="Participants Forms Access"
									to={`${courseSlug}/participant-forms-access`}
									icon="description"
								/>
								<QuickLink
									text="Attendance Register"
									to={`${courseSlug}/attendance-register`}
									icon="how_to_reg"
								/>
								{course.requires_written_assessment === 1 && (
									<QuickLink
										text="PMVA Assessments"
										to={`${courseSlug}/written-assessments`}
										icon="contract_edit"
									/>
								)}
								{course.requires_daily_risk_assessment === 1 && (
									<QuickLink
										text="Daily Risk Assessment"
										to={`${courseSlug}/daily-risk-assessment`}
										icon="edit_square"
									/>
								)}
								<QuickLink
									text="Teaching Log"
									to={`${courseSlug}/teaching-log`}
									icon="school"
								/>
								{(course?.requires_physical_competency === 1) && (
									<QuickLink
										text="Physical Competency Assessments"
										to={`${courseSlug}/attendance-register`}
										icon="thumbs_up_down"
									/>
								)}
								{course?.tutor_can_send_certificates === 1 && (
									<QuickLink
										text="Certificates"
										to={`${courseSlug}/certificates`}
										icon="verified"
									/>
								)}
								<QuickLink
									text="Incidents Witnessed"
									to={`${courseSlug}/incidents-witnessed`}
									icon="personal_injury"
								/>
							</div>
							<Seperator />
							<TitleHeader title="Other Forms" />
							<div className="course-quick-links">
								<QuickLink
									text="Referral Competency Form"
									to={`${courseSlug}/attendance-register`}
									icon="supervised_user_circle"
									type="light"
								/>
								<QuickLink
									text="Incident Form"
									to={`${courseSlug}/report-incident`}
									icon="personal_injury"
									type="light"
								/>
								<QuickLink
									text="Evaluation Forms"
									to={`${courseSlug}/evaluation-forms`}
									icon="grading"
									type="light"
								/>
							</div>
						</div>
						<div className="col-sm-4">
							<CourseChecklist />
							<CourseCommentsBox />
						</div>
					</div>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
}

export default CoursePage;
